/**
 * Fixed-height tools row under document chrome (Style · Format · Insert).
 *
 * Always present in Flow and Block so mode switch does not shift vertical
 * layout. On Block the menus are grayed (not removed).
 *
 * A reader who can't edit gets no menus at all (Olivia, 10 Oct: a greyed bar
 * promises tools they will never have): only `trailing`, or no row.
 */
import type { ReactNode } from "react";
import type { Editor } from "@tiptap/core";
import { EditorRibbon } from "./EditorRibbon";
import { cn } from "@/lib/cn";

/** Total strip height including bottom border — keep identical on Flow & Block. */
export const EDITOR_TOOLS_STRIP_H = 31;

export function EditorToolsStrip({
  editor = null,
  onDeleteBlock,
  enabled = true,
  readOnly = false,
  trailing,
  className,
}: {
  editor?: Editor | null;
  onDeleteBlock?: () => void;
  /** False on Block — same row, grayed, no interaction. */
  enabled?: boolean;
  /** The reader can't edit this document: no menus, greyed or otherwise. */
  readOnly?: boolean;
  trailing?: ReactNode;
  className?: string;
}) {
  if (readOnly && !trailing) return null;
  return (
    <div
      data-testid="editor-tools-strip"
      data-read-only={readOnly ? "true" : undefined}
      data-enabled={enabled ? "true" : "false"}
      className={cn(
        "flex shrink-0 items-center gap-2 border-b border-[var(--lilia-border)] bg-[var(--lilia-surface)]/80 px-2",
        className,
      )}
      style={{ height: EDITOR_TOOLS_STRIP_H }}
    >
      {readOnly ? (
        <div className="min-w-0 flex-1" />
      ) : (
      <div
        className={cn(
          "min-w-0 flex-1 overflow-x-auto transition-opacity duration-150",
          !enabled && "opacity-[0.38] pointer-events-none select-none",
        )}
        aria-disabled={!enabled}
        title={enabled ? undefined : "Formatting menus are available in Edit"}
      >
        <EditorRibbon
          editor={enabled ? editor : null}
          onDeleteBlock={enabled ? onDeleteBlock : undefined}
          className="!h-[30px] !border-0 !bg-transparent !px-0 min-w-0"
        />
      </div>
      )}
      {trailing ? <div className="shrink-0 flex items-center gap-1.5">{trailing}</div> : null}
    </div>
  );
}
